import { useEffect, useRef, useState } from "react";
import * as Icons from "lucide-react";

/**
 * Floating bottom-right chat bubble that lazy-loads the realtor's
 * Magicalnodes chatbot iframe on first open.
 *
 * - Renders nothing if no URL.
 * - Iframe is only mounted on first open (no perf hit until tapped).
 * - Mobile-friendly: drawer-style sheet under 640px, panel above.
 * - `context` is merged into the iframe URL as query params so the bot
 *   knows which listing the viewer is currently on (share page) or that
 *   the viewer is browsing a multi-listing portfolio (dashboard).
 */
export default function RealtorChatbotBubble({
  url,
  realtorName,
  brandColor,
  context,
}) {
  const [open, setOpen] = useState(false);
  const [hasOpened, setHasOpened] = useState(false); // mount iframe only after first click
  const panelRef = useRef(null);

  useEffect(() => {
    if (open && !hasOpened) setHasOpened(true);
  }, [open, hasOpened]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  if (!url || !/^https?:\/\//i.test(url)) return null;

  // Merge any caller-supplied context params into the iframe URL. We
  // preserve the URL the realtor pasted (with its key= and other params
  // intact) and just append our extras. Skips empty / nullish values so
  // the iframe URL stays clean.
  const buildIframeUrl = () => {
    try {
      const u = new URL(url);
      if (context && typeof context === "object") {
        Object.entries(context).forEach(([k, v]) => {
          if (v === null || v === undefined || v === "") return;
          // Coerce numbers / booleans / arrays to strings; join arrays with ", "
          const val = Array.isArray(v) ? v.join(", ") : String(v);
          if (val.trim()) u.searchParams.set(k, val);
        });
      }
      return u.toString();
    } catch {
      return url;
    }
  };

  const accent = brandColor || "#f59e0b"; // amber-500 fallback
  const firstName = (realtorName || "").trim().split(/\s+/)[0] || "your agent";

  return (
    <>
      {/* Floating launcher button — bottom right.
          Sits above the LIVE badge (z-50) and below open modals (z-9998). */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="fixed bottom-6 right-6 z-[60] group flex items-center gap-2 pl-4 pr-5 py-3 rounded-full bg-black/90 backdrop-blur border border-white/15 text-white text-sm font-medium shadow-2xl hover:scale-[1.03] active:scale-95 transition-transform"
        style={{ boxShadow: `0 8px 24px -8px ${accent}55, 0 0 0 1px rgba(255,255,255,0.05)` }}
        aria-label={open ? "Close chat" : `Ask ${firstName} anything`}
        data-testid="realtor-chatbot-launcher"
      >
        <span
          className="relative flex items-center justify-center w-6 h-6 rounded-full"
          style={{ backgroundColor: `${accent}22`, color: accent }}
        >
          {open ? (
            <Icons.X size={14} strokeWidth={2.5} />
          ) : (
            <Icons.Sparkles size={14} strokeWidth={2.5} />
          )}
          {!open && (
            <span
              className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full animate-pulse"
              style={{ backgroundColor: accent }}
            />
          )}
        </span>
        <span className="hidden sm:inline whitespace-nowrap">
          {open ? "Close" : "Ask AI"}
        </span>
      </button>

      {/* Panel — desktop right-anchored, mobile bottom-sheet */}
      {open && (
        <>
          {/* mobile-only scrim so taps outside close the sheet */}
          <div
            className="fixed inset-0 z-[55] bg-black/40 backdrop-blur-sm sm:hidden"
            onClick={() => setOpen(false)}
            aria-hidden
          />
          <div
            ref={panelRef}
            className="fixed z-[56] bg-[#0A0A0A] border border-white/10 shadow-2xl overflow-hidden flex flex-col
              inset-x-0 bottom-0 rounded-t-2xl
              sm:inset-x-auto sm:bottom-24 sm:right-6 sm:w-[380px] sm:rounded-2xl"
            style={{
              maxHeight: "min(620px, 85vh)",
              height: "min(620px, 85vh)",
            }}
            data-testid="realtor-chatbot-panel"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/5">
              <div className="flex items-center gap-2 min-w-0">
                <span
                  className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: `${accent}1f`, color: accent }}
                >
                  <Icons.Sparkles size={14} strokeWidth={2.5} />
                </span>
                <div className="min-w-0">
                  <div className="text-[13px] text-white font-medium truncate">
                    Ask {firstName}'s AI
                  </div>
                  <div className="text-[10px] text-zinc-500 truncate">
                    Available 24/7 · powered by Magicalnodes
                  </div>
                </div>
              </div>
              <button
                onClick={() => setOpen(false)}
                className="p-1.5 rounded-md text-zinc-500 hover:text-white hover:bg-white/5 transition-colors"
                aria-label="Close chat"
                data-testid="realtor-chatbot-close"
              >
                <Icons.X size={15} />
              </button>
            </div>

            {/* Iframe area — only mounts after first open */}
            <div className="flex-1 bg-black">
              {hasOpened ? (
                <iframe
                  src={buildIframeUrl()}
                  title={`${firstName}'s AI assistant`}
                  className="w-full h-full border-0"
                  allow="clipboard-write"
                  data-testid="realtor-chatbot-iframe"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Icons.Loader2 size={18} className="animate-spin text-zinc-600" />
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </>
  );
}
